import styled from '@emotion/styled';
import { GenericLazyString, gettext } from '@eventbrite/i18n';
import { DurationIcon, Typography } from '@eventbrite/marmalade';
import { formatDuration, intervalToDuration } from 'date-fns';
import React from 'react';
import { useTransientAutoCreatedEventContext } from '../../../DraftEventStoreProvider';
import { EventDateTime } from '../../../shared';

export enum AboutEventTestId {
    Container = 'AboutEventContainer',
}

export const AboutEvent = () => {
    const calculateEventDuration = (
        startTime: Omit<EventDateTime, 'local'>,
        endTime: Omit<EventDateTime, 'local'>,
    ): string => {
        const start = new Date(startTime.utc);
        const end = new Date(endTime.utc);
        const duration = intervalToDuration({ start, end });

        // returns the date in human readable format. eg. 2 hours and 30 minutes, 4 days
        let formattedDuration = formatDuration(duration);
        formattedDuration = formattedDuration.replace('0 hours and ', '');

        return formattedDuration;
    };

    const { transientAutoCreatedEvent } = useTransientAutoCreatedEventContext();

    if (!transientAutoCreatedEvent) return null;

    return (
        <AboutEventContainer
            aria-label="Details about the event including the date and description"
            data-testid={AboutEventTestId.Container}
        >
            <Typography as="p" color="neutral-900" variant="heading-sm">
                {gettext('About this event')}
            </Typography>
            <AboutEventBody>
                <AboutEventItem
                    icon={<DurationIcon size="small" color="#3659e3" />}
                    title={calculateEventDuration(
                        transientAutoCreatedEvent.start,
                        transientAutoCreatedEvent.end,
                    )}
                />
                <DescriptionContainer>
                    <Typography
                        as="p"
                        color="neutral-900"
                        variant="body-md"
                        style={{
                            lineHeight: '1.6',
                            marginBottom: '1rem',
                            textAlign: 'justify',
                        }}
                    >
                        <span
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML={{
                                __html: transientAutoCreatedEvent.description,
                            }}
                        />
                    </Typography>
                </DescriptionContainer>
            </AboutEventBody>
        </AboutEventContainer>
    );
};

const AboutEventItem = ({
    icon,
    title,
}: {
    icon: React.ReactNode;
    title: GenericLazyString | string;
}) => {
    return (
        <AboutEventItemContainer>
            <IconContainer>{icon}</IconContainer>
            <Typography as="p" color="ui-800" variant="body-md">
                {title}
            </Typography>
        </AboutEventItemContainer>
    );
};

const AboutEventContainer = styled.section`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const AboutEventBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 32px;
`;

const DescriptionContainer = styled.div`
    width: 70%;

    @media (max-width: 740px) {
        width: 100%;
    }
`;

const IconContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 8px;
    background-color: #f8f7fa;
`;

const AboutEventItemContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
`;
